/**
 * CORS-safe keepalive logger for best-effort background logging.
 * Uses fetch with keepalive: true and credentials: 'omit' to avoid
 * the CORS issue where sendBeacon uses "include" credentials mode
 * which rejects wildcard origins.
 */
import { getSupabaseRuntimeConfig } from '@/integrations/supabase/runtimeConfig';

const PENDING_LOGS_KEY = 'pendingKeepaliveLogs';

interface LogPayload {
  error_message: string;
  error_stack?: string | null;
  component_stack?: string | null;
  url?: string;
  user_agent?: string;
  user_id?: string | null;
  user_feedback?: string;
  device_info?: Record<string, unknown> | null;
  severity?: string;
  source?: string;
  error_type?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Send a log payload to the backend using fetch with keepalive.
 * This is CORS-safe because it uses credentials: 'omit'.
 * Falls back to localStorage if fetch fails.
 */
export async function sendLogWithKeepalive(payload: LogPayload): Promise<boolean> {
  const { url: supabaseUrl, key: supabaseKey } = getSupabaseRuntimeConfig();
  const supabaseAuthToken = supabaseKey;

  if (!supabaseUrl || !supabaseKey || !supabaseAuthToken) {
    console.warn('[keepaliveLogger] Missing Supabase config, storing locally');
    storeLogLocally(payload);
    return false;
  }

  try {
    const url = `${supabaseUrl}/functions/v1/log-client-error`;
    
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit', // Critical: prevents CORS wildcard + credentials conflict
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseAuthToken}`,
      },
    });

    if (!response.ok) {
      console.warn('[keepaliveLogger] Server returned error:', response.status);
      storeLogLocally(payload);
      return false;
    }

    return true;
  } catch (error) {
    console.warn('[keepaliveLogger] Fetch failed, storing locally:', error);
    storeLogLocally(payload);
    return false;
  }
}

/**
 * Fire-and-forget version that doesn't await the response.
 * Good for beforeunload handlers where you can't wait.
 */
export function sendLogWithKeepaliveSync(payload: LogPayload): void {
  const { url: supabaseUrl, key: supabaseKey } = getSupabaseRuntimeConfig();
  const supabaseAuthToken = supabaseKey;

  if (!supabaseUrl || !supabaseKey || !supabaseAuthToken) {
    storeLogLocally(payload);
    return;
  }

  try {
    const url = `${supabaseUrl}/functions/v1/log-client-error`;
    
    // Fire and forget - we don't await this
    fetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseAuthToken}`,
      },
    }).catch(() => {
      // Silent fail, already stored locally as backup
    });
  } catch {
    storeLogLocally(payload);
  }
}

/**
 * Store a log payload in localStorage for later retry.
 */
function storeLogLocally(payload: LogPayload): void {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_LOGS_KEY) || '[]');
    pending.push({
      ...payload,
      stored_at: new Date().toISOString(),
    });
    // Keep only the last 20 entries to prevent storage bloat
    localStorage.setItem(PENDING_LOGS_KEY, JSON.stringify(pending.slice(-20)));
  } catch (e) {
    console.error('[keepaliveLogger] Failed to store log locally:', e);
  }
}

/**
 * Flush any pending logs from localStorage.
 * Call this on app initialization.
 */
export async function flushPendingLogs(): Promise<void> {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_LOGS_KEY) || '[]');
    if (pending.length === 0) return;

    console.log(`[keepaliveLogger] Flushing ${pending.length} pending logs`);
    
    // Clear immediately to prevent duplicate sends
    localStorage.removeItem(PENDING_LOGS_KEY);

    for (const log of pending) {
      await sendLogWithKeepalive(log);
    }
  } catch (e) {
    console.error('[keepaliveLogger] Failed to flush pending logs:', e);
  }
}
